import {
  Box,
  IconButton as MuiIconButton,
  Typography,
  Collapse
} from '@mui/material'
import {
  CaretDownIcon,
  ClockIcon,
  CloseIcon,
  MemberDownIcon
} from 'assets/icons'
import IconButton from 'components/buttons/IconButton'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { IEventDetails } from 'hooks/useEventDetails'
import { t } from 'i18next'
import { Controller } from 'react-hook-form'
import Logger from 'services/Logger'
import { EventDoc } from 'types/api/changes'
import { Model } from 'types/model'
import { useEffect, useState, useLayoutEffect } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import TimezonePopup from './TimezonePopup'
import { getDefaultTimezone } from 'index'
import { theme } from 'theme'
import { isAMPM } from 'utils/date'
import {
  EventDatePicker,
  PickerType
} from 'components/eventComponents/EventDatePicker'
import { is } from 'cypress/types/bluebird'
import i18n from 'i18n'

dayjs.extend(utc)
dayjs.extend(timezone)

interface StartEndDateRowsProps {
  eventHook: IEventDetails
  event: Model<EventDoc> | EventDoc
  timeZoneOpen: boolean
  setTimeZoneOpen: (open: boolean) => void
  removeTimeZone: () => void
  nonDefaultTimeZone: boolean
}

// Format date based on mode: collapsed (year only if different) or expanded (always year)
function formatDate(
  timestamp: number,
  timeZone: string | null | undefined,
  showYear: boolean
): string {
  const tz = timeZone && timeZone.length > 0 ? timeZone : getDefaultTimezone()
  const date = dayjs(timestamp).tz(tz)
  const currentYear = dayjs().year()
  const dateYear = date.year()

  // Format: "Wed, May 27" or "Wed, May 27, 2027"
  if (showYear || dateYear !== currentYear) {
    return date.format('ddd, MMM D, YYYY')
  }
  return date.format('ddd, MMM D')
}

// Format time with lowercase am/pm
function formatTime(
  timestamp: number,
  timeZone: string | null | undefined
): string {
  const tz = timeZone && timeZone.length > 0 ? timeZone : getDefaultTimezone()
  const date = dayjs(timestamp).tz(tz)

  if (!isAMPM()) {
    return date.format('HH:mm')
  }

  // Format with lowercase am/pm
  const hours = date.hour()
  const minutes = date.minute()
  const period = hours >= 12 ? 'pm' : 'am'
  const displayHours = hours % 12 || 12
  const minutesStr = minutes.toString().padStart(2, '0')

  return `${displayHours}:${minutesStr}${period}`
}

export default function StartEndDateRows(props: StartEndDateRowsProps) {
  const {
    eventHook,
    event,
    timeZoneOpen,
    setTimeZoneOpen,
    removeTimeZone,
    nonDefaultTimeZone
  } = props

  const [expanded, setExpanded] = useState(false)

  const isAllDay = eventHook.getValues('AllDay') === '1'
  const isSomeday = eventHook.getValues('someday') === '1'
  const timeZone = eventHook.getValues('TimeZoneNameID')

  const startTimestamp = Number(eventHook.getValues('StartDate')) * 1000
  const endTimestamp = Number(eventHook.getValues('EndDate')) * 1000

  // Collapsed mode for timed events - single line with clock icon
  // Each element (date, start time, end time) is independently clickable with its own picker
  const tz = timeZone && timeZone.length > 0 ? timeZone : getDefaultTimezone()
  const start = dayjs(startTimestamp).tz(tz)
  const end = dayjs(endTimestamp).tz(tz)
  const sameDay = start.isSame(end, 'day')

  // Determine if should be expanded based on current conditions
  // Multi-day non-all-day events should always be expanded
  const shouldBeExpanded = !sameDay && !isAllDay
  // isExpanded is true if: user manually expanded OR if it's a multi-day non-all-day event
  // isExpanded is false if: all-day is on
  const isExpanded = isAllDay ? false : shouldBeExpanded || expanded

  useLayoutEffect(() => {
    // Keep expanded state in sync for user-triggered expansions
    if (shouldBeExpanded && !expanded) {
      setExpanded(true)
    }
  }, [shouldBeExpanded, expanded])

  return (
    <>
      {/* Collapsed view */}
      <Collapse in={!isExpanded} timeout={300}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          width="100%"
          minHeight="30px"
        >
          {/* Clock icon */}
          <Box
            sx={{
              minWidth: '42px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}
          >
            <ClockIcon
              style={{
                width: 16,
                height: 16,
                color: theme.palette.input.placeholder
              }}
            />
          </Box>

          {/* Date/Time display - each element independently clickable */}
          <Box display="flex" flexDirection="row" alignItems="center" flex={1}>
            {/* Start Date Picker (date only) */}
            <Controller
              name="StartDate"
              control={eventHook.control}
              defaultValue={event.StartDate}
              render={({ field }) => (
                <EventDatePicker
                  eventHook={eventHook}
                  pickerType={PickerType.START}
                  allDay={isAllDay}
                  setStartTime={eventHook.setStartTime}
                  startTime={Number(field.value) * 1000}
                  setEndTime={eventHook.setEndTime}
                  endTime={endTimestamp}
                  timeZone={timeZone}
                  setTimeZone={eventHook.setTimeZone}
                  duration={endTimestamp - Number(field.value) * 1000}
                  dateFormat={(ts: number) => formatDate(ts, timeZone, false)}
                  hideTime
                />
              )}
            />

            <Box height={'100%'} width={'0.5rem'} />
            {/* Start Time Picker (time only) */}
            <Controller
              name="StartDate"
              control={eventHook.control}
              defaultValue={event.StartDate}
              render={({ field }) => (
                <EventDatePicker
                  shouldShowTimeDropdown
                  eventHook={eventHook}
                  pickerType={PickerType.START}
                  allDay={isAllDay}
                  setStartTime={eventHook.setStartTime}
                  startTime={Number(field.value) * 1000}
                  setEndTime={eventHook.setEndTime}
                  endTime={endTimestamp}
                  timeZone={timeZone}
                  setTimeZone={eventHook.setTimeZone}
                  duration={endTimestamp - Number(field.value) * 1000}
                  timeFormat={(ts: number) => formatTime(ts, timeZone)}
                  hideDate
                />
              )}
            />

            <Typography
              sx={{ fontSize: '14px', color: theme.palette.text.primary }}
            >
              –
            </Typography>

            {/* End Time/Date Picker */}
            {sameDay || isAllDay ? (
              // Same day: only show end time picker
              <Controller
                name="EndDate"
                control={eventHook.control}
                defaultValue={event.EndDate}
                render={({ field }) => (
                  <EventDatePicker
                    shouldShowTimeDropdown
                    eventHook={eventHook}
                    pickerType={PickerType.END}
                    allDay={isAllDay}
                    setStartTime={eventHook.setStartTime}
                    startTime={startTimestamp}
                    setEndTime={eventHook.setEndTime}
                    endTime={Number(field.value) * 1000}
                    timeZone={timeZone}
                    setTimeZone={eventHook.setTimeZone}
                    duration={Number(field.value) * 1000 - startTimestamp}
                    timeFormat={(ts: number) => formatTime(ts, timeZone)}
                    dateFormat={(ts: number) => formatDate(ts, timeZone, false)}
                    hideDate={!isAllDay}
                  />
                )}
              />
            ) : (
              // Different days: show end date and time picker separately
              <>
                <Controller
                  name="EndDate"
                  control={eventHook.control}
                  defaultValue={event.EndDate}
                  render={({ field }) => (
                    <EventDatePicker
                      eventHook={eventHook}
                      pickerType={PickerType.END}
                      allDay={isAllDay}
                      setStartTime={eventHook.setStartTime}
                      startTime={startTimestamp}
                      setEndTime={eventHook.setEndTime}
                      endTime={Number(field.value) * 1000}
                      timeZone={timeZone}
                      setTimeZone={eventHook.setTimeZone}
                      duration={Number(field.value) * 1000 - startTimestamp}
                      dateFormat={(ts: number) =>
                        formatDate(ts, timeZone, false)
                      }
                      hideTime
                    />
                  )}
                />
                <Controller
                  name="EndDate"
                  control={eventHook.control}
                  defaultValue={event.EndDate}
                  render={({ field }) => (
                    <EventDatePicker
                      shouldShowTimeDropdown
                      eventHook={eventHook}
                      pickerType={PickerType.END}
                      allDay={isAllDay}
                      setStartTime={eventHook.setStartTime}
                      startTime={startTimestamp}
                      setEndTime={eventHook.setEndTime}
                      endTime={Number(field.value) * 1000}
                      timeZone={timeZone}
                      setTimeZone={eventHook.setTimeZone}
                      duration={Number(field.value) * 1000 - startTimestamp}
                      timeFormat={(ts: number) => formatTime(ts, timeZone)}
                      hideDate
                    />
                  )}
                />
              </>
            )}
            <Box height={'100%'} width={'0.5rem'} />
            {/* Time zone - show for non all-day events */}
            {!isSomeday && !isAllDay && (
              <Controller
                name="StartDate"
                control={eventHook.control}
                defaultValue={event.StartDate}
                render={({ field }) => {
                  Logger.blue(
                    'Timezone:',
                    eventHook.getValues('TimeZoneNameID')
                  )
                  return (
                    <TimezonePopup
                      open={timeZoneOpen}
                      setOpen={setTimeZoneOpen}
                      setTimeZone={(tz: string) => {
                        eventHook.setTimeZone(tz)
                      }}
                    >
                      <Box
                        sx={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          marginLeft: i18n.dir() === 'ltr' ? '4px' : undefined,
                          marginRight: i18n.dir() === 'ltr' ? undefined : '4px',
                          gap: 1,
                          paddingBottom: '3px'
                        }}
                        onClick={() => setTimeZoneOpen(true)}
                      >
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: nonDefaultTimeZone
                              ? theme.palette.text.primary
                              : theme.palette.input.placeholder,
                            padding: '3px 4px',
                            '&:hover': {
                              backgroundColor: theme.palette.background.paper,
                              borderRadius: '8px'
                            }
                          }}
                        >
                          {nonDefaultTimeZone ? timeZone : t('timeZone')}
                        </Typography>
                        {nonDefaultTimeZone && (
                          <IconButton size="xs" onClick={removeTimeZone}>
                            <CloseIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TimezonePopup>
                  )
                }}
              />
            )}
          </Box>

          {/* Expand button - only show when not expanded */}
          {!isSomeday && !isAllDay && !isExpanded && (
            <MuiIconButton
              size="small"
              onClick={() => setExpanded(true)}
              sx={{
                padding: '4px',
                marginLeft: 'auto'
              }}
            >
              <MemberDownIcon
                style={{
                  width: 24,
                  height: 24,
                  color: theme.palette.input.placeholder
                }}
              />
            </MuiIconButton>
          )}
        </Box>
      </Collapse>

      {/* Expanded view */}
      <Collapse in={isExpanded} timeout={300}>
        <Box>
          {/* Starts row */}
          <PopupSectionRow
            gap="0rem"
            icon={
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400
                }}
              >
                {t('starts')}
              </Typography>
            }
            title={
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={2}
              >
                <Controller
                  name="StartDate"
                  control={eventHook.control}
                  defaultValue={event.StartDate}
                  render={({ field }) => (
                    <EventDatePicker
                      eventHook={eventHook}
                      pickerType={PickerType.START}
                      allDay={isAllDay}
                      setStartTime={eventHook.setStartTime}
                      startTime={Number(field.value) * 1000}
                      setEndTime={eventHook.setEndTime}
                      endTime={endTimestamp}
                      timeZone={timeZone}
                      setTimeZone={eventHook.setTimeZone}
                      duration={endTimestamp - Number(field.value) * 1000}
                      dateFormat={(ts: number) =>
                        formatDate(ts, timeZone, true)
                      }
                      timeFormat={(ts: number) => formatTime(ts, timeZone)}
                    />
                  )}
                />

                {/* Time zone */}
                {!isSomeday && !isAllDay && (
                  <Controller
                    name="StartDate"
                    control={eventHook.control}
                    defaultValue={event.StartDate}
                    render={({ field }) => {
                      return (
                        <TimezonePopup
                          open={timeZoneOpen && isExpanded}
                          setOpen={setTimeZoneOpen}
                          setTimeZone={(tz: string) => {
                            eventHook.setTimeZone(tz)
                          }}
                        >
                          <Box
                            sx={{
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                            onClick={() => setTimeZoneOpen(true)}
                          >
                            <Typography
                              sx={{
                                fontSize: '14px',
                                color: nonDefaultTimeZone
                                  ? theme.palette.text.primary
                                  : theme.palette.input.placeholder,
                                paddingBottom: '3px',
                                '&:hover': {
                                  backgroundColor:
                                    theme.palette.background.paper
                                }
                              }}
                            >
                              {nonDefaultTimeZone ? timeZone : t('timeZone')}
                            </Typography>
                            {nonDefaultTimeZone && (
                              <IconButton size="xs" onClick={removeTimeZone}>
                                <CloseIcon />
                              </IconButton>
                            )}
                          </Box>
                        </TimezonePopup>
                      )
                    }}
                  />
                )}
              </Box>
            }
          />

          {/* Ends row */}
          <PopupSectionRow
            gap="0rem"
            onlyTitle
            icon={
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400
                }}
              >
                {t('ends')}
              </Typography>
            }
            title={
              <Controller
                name="EndDate"
                control={eventHook.control}
                defaultValue={event.EndDate}
                render={({ field }) => (
                  <EventDatePicker
                    shouldShowTimeDropdown
                    eventHook={eventHook}
                    pickerType={PickerType.END}
                    allDay={isAllDay}
                    setStartTime={eventHook.setStartTime}
                    startTime={startTimestamp}
                    setEndTime={eventHook.setEndTime}
                    endTime={Number(field.value) * 1000}
                    timeZone={timeZone}
                    setTimeZone={eventHook.setTimeZone}
                    duration={Number(field.value) * 1000 - startTimestamp}
                    dateFormat={(ts: number) => formatDate(ts, timeZone, true)}
                    timeFormat={(ts: number) => formatTime(ts, timeZone)}
                  />
                )}
              />
            }
          />
        </Box>
      </Collapse>
    </>
  )
}
